import { useState, useRef } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../contexts/ToastContext';
import './ProfilePhotoUpload.css';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string | null;
  onPhotoUpdate: (photoUrl: string) => void;
}

export default function ProfilePhotoUpload({ currentPhotoUrl, onPhotoUpdate }: ProfilePhotoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToastContext();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('ファイルサイズは5MB以下にしてください');
      return;
    }

    // ファイル形式チェック
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      showError('JPEG、PNG、WebP、GIF形式の画像を選択してください');
      return;
    }

    setSelectedFile(file);
    
    // プレビュー用のURLを作成
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
      setShowModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentUser) return;

    setIsUploading(true);
    try {
      // 古い写真を削除（存在する場合）
      if (currentPhotoUrl) {
        const oldPath = currentPhotoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('profile')
            .remove([`${currentUser.id}/${oldPath}`]);
        }
      }

      // 新しいファイル名を生成
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${currentUser.id}/${fileName}`;

      // ファイルをアップロード
      const { error: uploadError } = await supabase.storage
        .from('profile')
        .upload(filePath, selectedFile);

      if (uploadError) {
        throw uploadError;
      }

      // 公開URLを取得
      const { data: { publicUrl } } = supabase.storage
        .from('profile')
        .getPublicUrl(filePath);

      // データベースのユーザー情報を更新
      const { error: updateError } = await supabase
        .from('users')
        .update({ profile_photo: publicUrl })
        .eq('id', currentUser.id);

      if (updateError) {
        throw updateError;
      }

      // 親コンポーネントに通知
      onPhotoUpdate(publicUrl);
      
      // モーダルを閉じる
      setShowModal(false);
      setPreviewUrl(null);
      setSelectedFile(null);
      
     showSuccess('プロフィール写真を更新しました！');
    } catch (error) {
      console.error('Upload error:', error);
    showError('アップロードに失敗しました。もう一度お試しください。');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <div className="profile-photo-upload" onClick={triggerFileSelect}>
        <div className="photo-container">
          {currentPhotoUrl ? (
            <img src={currentPhotoUrl} alt="プロフィール写真" className="profile-photo" />
          ) : (
            <div className="default-avatar">
              <Camera size={32} />
            </div>
          )}
          <div className="upload-overlay">
            <Camera size={20} />
            <span>写真を変更</span>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {showModal && (
        <div className="upload-modal-overlay" onClick={handleCancel}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>プロフィール写真を設定</h3>
              <button className="close-button" onClick={handleCancel}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {previewUrl && (
                <div className="preview-container">
                  <img src={previewUrl} alt="プレビュー" className="preview-image" />
                </div>
              )}
              
              <div className="file-info">
                <p className="file-name">{selectedFile?.name}</p>
                <p className="file-size">
                  {selectedFile && `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
                </p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCancel} disabled={isUploading}>
                キャンセル
              </button>
              <button 
                className="upload-button" 
                onClick={handleUpload} 
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Upload size={16} />
                    アップロード中...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    アップロード
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}